<template>
    <Card :title="$t('labels.category')">
        <div class="card-body">
            <div class="row">
                <div class="col-lg-4">
                    <FormGroup :label="$t('categoryCard.labels.source_category')">
                        <Input :model-value="categorySource?.name" disabled/>
                    </FormGroup>
                </div>
                <div class="col-lg-4" v-for="lang of integration.language_aliases">
                    <FormGroup :label="$t('labels.name') + ` (${ $t(`shortLang.${lang}`)})`">
                        <Input
                            v-model="category.names[lang]"
                            :disabled="categorySource?.key === CategorySourceEnum.SERVICE"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-6" v-for="lang of integration.language_aliases" v-if="integration.service === IntegrationServiceEnum.PROM">
                    <FormGroup :label="$t('labels.seo') + ` (${ $t(`shortLang.${lang}`)})`">
                        <InputTag
                            v-model="category.seo_keywords[lang]"
                        />
                    </FormGroup>
                </div>
                <div class="col-12 d-flex flex-wrap" style="gap: .5rem;" v-if="integration.service === IntegrationServiceEnum.PROM">
                    <Button color="teal" @click="$refs.ProductKeywordsModal.show()">
                        <i class="icon-statistics mr-2"></i>
                        {{ $t('buttons.analyze') }}
                    </Button>
                </div>
            </div>
        </div>
        <div v-if="!isSupplier" class="card-footer d-flex justify-content-end flex-wrap" style="gap: .5rem">
            <Button color="light" @click="remove">
                <i class="icon-trash mr-2"></i>
                <span>{{ $t('buttons.delete') }}</span>
            </Button>
            <Button color="indigo" @click="update">
                <i class="icon-pencil7 mr-2"></i>
                {{ $t("buttons.edit") }}
            </Button>
        </div>
    </Card>
    <Card>
        <NavTab class="nav-tabs-bottom mb-0" has-wrapper>
            <NavTabItem :href="`#${tab.id}`" :ref="setTabRef" v-for="tab in tabs" :key="tab.id">{{ tab.name }}</NavTabItem>
            <template #content>
                <NavTabPane id="products">
                    <CategoryProductsTable ref="ProductsTable" :integration="integration" :category="category" :filters="filters" />
                </NavTabPane>
                <NavTabPane id="filters">
                    <CategoryFiltersTable ref="FiltersTable" :integration="integration" :category="category" @filters:updated="handleFiltersUpdated" />
                </NavTabPane>
            </template>
        </NavTab>
    </Card>
    <ProductKeywordsModal ref="ProductKeywordsModal" :category="category" :integration-languages="integration.language_aliases" />
</template>
<script>
import Card from "../../../../../../components/new/CardComponent.vue";
import NavTab from "../../../../../components/tabs/NavTab.vue";
import NavTabItem from "../../../../../components/tabs/NavTabItem.vue";
import NavTabPane from "../../../../../components/tabs/NavTabPane.vue";
import Button from "../../../../../../components/new/button/AppButton.vue";
import Input from "../../../../../../components/new/form/Input.vue";
import FormGroup from "../../../../../../components/new/form/group/FormGroup.vue";
import InputTag from "../../../../../../components/new/form/Tags.vue";
import ProductKeywordsModal from "../../../modals/ProductKeywordsModal.vue";
import CategoryProductsTable from "./CategoryProductsTable.vue";
import CategoryFiltersTable from "./CategoryFiltersTable.vue";

import { httpClient } from "../../../../../../services/HttpClient";
import MessageNotificator from "../../../../../../services/MessageNotificator";
import { CategorySourceEnum } from '../../../../../enums/CategorySourceEnum';
import { IntegrationServiceEnum } from '../../../../../enums/IntegrationServiceEnum';

export default {
    name: "EditCategoryCard",
    components: {
        Card, Button, Input, FormGroup, NavTab, NavTabItem, NavTabPane,
        InputTag, ProductKeywordsModal, CategoryProductsTable, CategoryFiltersTable
    },
    props: {
        integration: {
            type: Object,
            required: true
        },
        projectUuid: {
            type: String,
            required: true
        },
        integrationUuid: {
            type: String,
            required: true
        },
        categoryUuid: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            IntegrationServiceEnum,
            CategorySourceEnum,
            category: {
                names: {},
                seo_keywords: {}
            },
            filters: [],
            tabs: [
                { id: 'filters', name: this.$t('labels.filters') },
                { id: 'products', name: this.$t('labels.publications') }
            ],
            tabRefs: [],
            categorySource: null
        }
    },
    methods: {
        setTabRef(el) {
            if (el) {
                this.tabRefs.push(el);
            }
        },
        async fetchCategory() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/categories/${this.$route.params.categoryUuid}`).then((response) => {
                this.category = response.data.data
            })
        },
        async fetchFilters() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                integration_category_uuids: [this.category.uuid],
                limit: 0,
                orders: [{
                    column: "name",
                    dir: "asc"
                }]
            }).then((response) => {
                this.filters = response.data.data
            })
        },
        update() {
            let requestData = {
                names: this.category.names
            }

            let seo = Object.entries(this.category.seo_keywords).reduce((acc, [alias, translation]) => {
                if (translation) {
                    acc[alias] = translation;
                } else {
                    acc[alias] = null;
                }
                return acc;
            }, {});

            if (Object.keys(seo).length > 0) {
                requestData.seo_keywords = seo;
            }

            httpClient.patch(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/categories/${this.$route.params.categoryUuid}`, requestData).then((response) => {
                this.category = response.data.data
                new MessageNotificator().success(this.$t("alerts.success"))
            })
        },
        remove() {
            new MessageNotificator().confirm(this.$t("alerts.confirm_delete"), () => {
                httpClient.delete(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/categories/${this.$route.params.categoryUuid}`).then(() => {
                    new MessageNotificator().success(this.$t("alerts.success"))
                    this.$router.push({name: "CategoriesCard"})
                })
            });
        },
        handleFiltersUpdated() {
            this.fetchFilters();
            this.$refs.ProductsTable.setProductsTableColumns();
        }
    },
    computed: {
        isSupplier() {
            return this.integration.service === IntegrationServiceEnum.SUPPLIER
        },
        mainIntegrationLanguages() {
            return this.integration.language_aliases[0];
        }
    },
    async mounted() {
        await this.fetchCategory()

        this.categorySource = this.category.source_id
            ? {
                key: CategorySourceEnum.SERVICE,
                name: this.$t('categoryCard.labels.from_service')
            }
            : {
                key: CategorySourceEnum.CUSTOM,
                name: this.$t('categoryCard.labels.custom')
            };

        await this.fetchFilters()

        this.$nextTick(() => {
            this.tabRefs[0].click()
        });
    },
    beforeUpdate() {
        this.tabRefs = []
    },
}
</script>
